/**
 * myWeek — the pure policy layer for MyWeekBand (SPICE Train A2's "My
 * week"). Mirrors cmHome.ts's split: the server returns the week's days
 * Monday..Sunday in chronological order; ordering/display policy for the
 * screen (today-first, weekday names, the empty state) lives here, pure
 * and unit-tested, no fetches, no React.
 */

import type { CmHomeMyWeekDayWire, CmHomeMyWeekNotThisWeekWire, CmHomeMyWeekWire } from '@/lib/cmHomeApi';
import { WEEKDAYS, proseDate } from '@/lib/proseDate';

export interface OrderedMyWeekDay extends CmHomeMyWeekDayWire {
  is_today: boolean;
}

/**
 * The week's days, TODAY FIRST, the rest kept in their original
 * Monday..Sunday order. Saturday and Sunday show only when a visit falls
 * on them, the week grid's own rule. When today falls outside the days
 * shown (a weekend read, or a stale cache), nothing matches and the days
 * stay in their normal chronological order: there is no "today" to
 * promote, not an error.
 */
export function orderMyWeekDays(myWeek: CmHomeMyWeekWire, todayIso: string): OrderedMyWeekDay[] {
  const days = myWeek.days
    .filter((d) => d.weekday <= 5 || d.visits.length > 0)
    .map((d) => ({ ...d, is_today: d.date === todayIso }));
  const todayIndex = days.findIndex((d) => d.is_today);
  if (todayIndex <= 0) return days;
  return [days[todayIndex]!, ...days.slice(0, todayIndex), ...days.slice(todayIndex + 1)];
}

/** ISO weekday (Monday 1 .. Sunday 7) to its name. WEEKDAYS is
 *  Sunday-indexed, so Sunday's 7 reads index 0. */
export function myWeekdayName(weekday: number): string {
  return WEEKDAYS[weekday % 7] ?? '';
}

const NOT_THIS_WEEK_STATUS_WORD: Record<CmHomeMyWeekNotThisWeekWire['status'], string> = {
  covered: 'Covered',
  on_hold: 'Paused',
  vacation: 'Vacation',
  hospital: 'Hospital',
};

/**
 * "Paused" / "Vacation until August 27, 2026": a plan's status word in
 * prose, read off the status word the wire carries. The screens say an
 * away plan through formatSpiceAway (src/lib/spiceScheduleCopy.ts).
 */
export function notThisWeekLabel(entry: Pick<CmHomeMyWeekNotThisWeekWire, 'status' | 'status_until'>): string {
  const word = NOT_THIS_WEEK_STATUS_WORD[entry.status];
  return entry.status_until ? `${word} until ${proseDate(entry.status_until)}` : word;
}

/** Whether the whole week (every day) has no planned visits AND no
 *  client is covered or paused either: the band's empty state. */
export function myWeekIsEmpty(myWeek: CmHomeMyWeekWire): boolean {
  return myWeek.days.every((d) => d.visits.length === 0) && myWeek.not_this_week.length === 0;
}
